import parse from "html-react-parser";
import { QRCodeSVG } from "qrcode.react";
import { useEffect, useState } from "react";
import CopyToClipboard from "react-copy-to-clipboard";
import {
  LuChevronDown,
  LuCopy,
  LuDownload,
  LuLoaderCircle,
  LuTriangleAlert,
} from "react-icons/lu";
import {
  BuildJobStatus,
  BuildJobStatusSchema,
  BuildType,
  BuildTypeSchema,
} from "../../../../darvin-shared/schema/zod/build";
import {
  BuildPlatform,
  useProjectBuildActions,
} from "../../../core_stores/useProjectBuildsStore";
import { useProjectActions } from "../../../core_stores/useProjectStore";
import { useSnackbar } from "../../../providers/SnackbarProvider";
import { formatDateToLocal } from "../../../utils/dateFormatter";
import { useChatMessagesActions } from "../../../view_stores/chat_messages/useChatMessagesStore";
import { useIsProjectProcessing } from "../../../view_stores/useProjectStatusStore";

export default function ProjectPublishBuildItem({
  buildId,
  lastPublished,
  type,
  selectedPlatform,
  status,
  isExpanded: initialIsExpanded = false,
}: {
  buildId: string;
  lastPublished: string;
  type: BuildType;
  selectedPlatform: BuildPlatform;
  status: BuildJobStatus;
  isExpanded?: boolean;
}) {
  const isProjectProcessing = useIsProjectProcessing();

  const { loadAndroidBuildUrl, loadErrorMessage } = useProjectBuildActions();
  const { addTemporaryMessage } = useChatMessagesActions();
  const { sendMessage } = useProjectActions();
  const { showSnackbar } = useSnackbar();

  const [isExpanded, setIsExpanded] = useState(initialIsExpanded);
  const [downloadUrl, setDownloadUrl] = useState<string | null | undefined>(
    null
  );
  const [errorMessage, setErrorMessage] = useState<string | null | undefined>(
    null
  );
  const [isErrorMessageSubmitting, setIsErrorMessageSubmitting] =
    useState(false);

  const isLoading =
    status === BuildJobStatusSchema.Enum.waiting ||
    status === BuildJobStatusSchema.Enum.running;

  useEffect(() => {
    if (canExpand() && isExpanded && downloadUrl === null) {
      loadDetails();
    }
  }, [isExpanded, status]);

  const canExpand = () => {
    if (type === BuildTypeSchema.Enum.web) {
      return status === BuildJobStatusSchema.Enum.error;
    }

    return (
      status === BuildJobStatusSchema.Enum.complete ||
      status === BuildJobStatusSchema.Enum.error
    );
  };

  const loadDetails = async () => {
    if (
      type === BuildTypeSchema.Enum.apk ||
      type === BuildTypeSchema.Enum.aab
    ) {
      if (status === BuildJobStatusSchema.Enum.complete) {
        const downloadUrl = await loadAndroidBuildUrl(buildId);
        setDownloadUrl(downloadUrl === null ? undefined : downloadUrl);
      } else if (status === BuildJobStatusSchema.Enum.error) {
        const errorMessage = await loadErrorMessage(buildId);
        setErrorMessage(errorMessage === null ? undefined : errorMessage);
      }
    } else if (type === BuildTypeSchema.Enum.web) {
      const errorMessage = await loadErrorMessage(buildId);
      setErrorMessage(errorMessage === null ? undefined : errorMessage);
    }
  };

  const tryToFix = async () => {
    setIsErrorMessageSubmitting(true);
    const message = `Fix compilation error: ${errorMessage!}`;
    addTemporaryMessage(message, null);
    await sendMessage(message, null);
    setIsErrorMessageSubmitting(false);
  };

  const getExpandedContent = () => {
    if (
      (status === BuildJobStatusSchema.Enum.complete && downloadUrl === null) ||
      (status === BuildJobStatusSchema.Enum.error && errorMessage === null)
    ) {
      return (
        <div className="flex items-center justify-center">
          <LuLoaderCircle className="animate-spin" size={24} />
        </div>
      );
    }

    if (
      status === BuildJobStatusSchema.Enum.complete &&
      downloadUrl === undefined
    ) {
      return (
        <div className="flex items-center justify-center">
          No available download URL
        </div>
      );
    }

    if (
      status === BuildJobStatusSchema.Enum.error &&
      errorMessage === undefined
    ) {
      return (
        <div className="flex items-center justify-center">
          No error message available
        </div>
      );
    }

    if (status === BuildJobStatusSchema.Enum.complete && downloadUrl) {
      return (
        <div className="flex flex-col items-center justify-center gap-4">
          <button
            className="project-publish-build-item-download w-full max-w-[300px] 
                flex items-center justify-center 
                gap-2 px-6 py-2 
              bg-green-500 hover:bg-green-600 text-white
              rounded-xl transition-colors mb-2"
            onClick={downloadBuild}
          >
            <span className="underline">Download</span>
            <LuDownload size={16} />
          </button>
          {type === BuildTypeSchema.Enum.apk && (
            <div
              className="p-5 bg-white rounded-lg
                  flex items-center justify-center mb-3"
            >
              <QRCodeSVG value={downloadUrl} />
            </div>
          )}
        </div>
      );
    }

    if (status === BuildJobStatusSchema.Enum.error && errorMessage) {
      return (
        <div className="flex flex-col gap-4">
          <div
            className="flex items-start gap-4 w-full min-w-0
        bg-[#F2F3FD] dark:bg-[#1E2023]
        rounded-lg p-4 border border-white dark:border-[#373B40]"
          >
            <div className="flex-1 min-w-0 max-h-[300px] overflow-y-auto">
              <span className="break-words hyphens-auto whitespace-pre-wrap text-sm block">
                {parse(errorMessage)}
              </span>
            </div>

            <div className="pt-1 flex-shrink-0">
              <CopyToClipboard
                text={errorMessage}
                onCopy={() => {
                  showSnackbar("Copied to clipboard");
                }}
              >
                <LuCopy
                  size={16}
                  className="hover:cursor-pointer hover:opacity-50 transition-opacity duration-300"
                />
              </CopyToClipboard>
            </div>
          </div>
          <div className="flex items-center justify-end">
            <button
              className="project-publish-build-item-try-to-fix bg-[#D6D9E7] dark:bg-dark-surface-tertiary/25
      border border-[#656A6E40] dark:border-[#373B40]
      rounded-full w-[87px] h-[34px]
      flex items-center justify-center
      hover:bg-[#FFFFFF] dark:hover:bg-dark-surface-tertiary
      transition-all duration-300"
              onClick={tryToFix}
              disabled={isErrorMessageSubmitting || isProjectProcessing}
            >
              {isErrorMessageSubmitting ? (
                <LuLoaderCircle className="animate-spin" size={16} />
              ) : (
                "Try to fix"
              )}
            </button>
          </div>
        </div>
      );
    }
  };

  const downloadBuild = async () => {
    if (downloadUrl) {
      const link = document.createElement("a");
      const fileName = downloadUrl.split("/").pop();
      link.setAttribute("download", fileName ?? "");
      link.href = downloadUrl;
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <div className="mb-4 overflow-hidden rounded-lg border border-white dark:border-[#373B40]">
      <div
        className={`px-4 flex items-center justify-between 
        gap-3 h-[50px] ${!isLoading && canExpand() && "hover:cursor-pointer"}`}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <span className="text-center">
          {selectedPlatform === BuildPlatform.ANDROID
            ? `Android ${type.toUpperCase()}`
            : "Web"}
        </span>
        <div
          className="flex items-center gap-2
            text-light-placeholder dark:text-dark-placeholder"
        >
          <span
            className={`flex items-center gap-1 ${
              status === BuildJobStatusSchema.Enum.error && "text-[#D8C640]"
            }`}
          >
            {status.charAt(0).toUpperCase() + status.slice(1)}
            {status === BuildJobStatusSchema.Enum.error && (
              <LuTriangleAlert size={16} />
            )}
          </span>
          {canExpand() && (
            <LuChevronDown
              size={16}
              className={`transition-transform duration-300 ease-in-out ${
                isExpanded ? "rotate-180" : ""
              }`}
            />
          )}
        </div>
        <span className="text-center text-light-placeholder dark:text-dark-placeholder">
          {formatDateToLocal(lastPublished)}
        </span>
      </div>

      {/* Collapsible content */}
      {!isLoading && canExpand() && (
        <div
          className={`overflow-hidden transition-all duration-500 ease-in-out ${
            isExpanded
              ? "max-h-[500px] opacity-100 mt-0"
              : "max-h-0 opacity-0 mt-0"
          }`}
        >
          <div
            className={`px-4 py-3 bg-white/5 dark:bg-black/10
            transform transition-transform duration-300 ease-in-out
            ${isExpanded ? "translate-y-0" : "-translate-y-2"}`}
          >
            {getExpandedContent()}
          </div>
        </div>
      )}
    </div>
  );
}
